import type { PropsWithChildren } from "react";
import { createContext, useContext, useState } from "react";

import type { Subscription } from "@/features/billing/models";
import type {
  Appointment,
  Provider,
  ScheduleAppointment,
} from "@/features/consultations/models";
import type { AvailableLanguage } from "@/shared/models";
import { NOW, PROVIDER_PREFERENCE } from "@/shared/models/legacy-constants";
import type { ObjectValue, USState } from "@/shared/models/legacy-types";
import { formatToBackendDate } from "@/shared/utils";

const defaultDateFromMonth = formatToBackendDate(
  new Date(NOW.getFullYear(), NOW.getMonth(), 1),
);

interface ScheduleDate {
  dateFromMonth: ScheduleAppointment["date"];
  appointmentDate?: ScheduleAppointment["date"];
  timeSlot?: ScheduleAppointment["timeSlot"];
}

interface ScheduleContextProps {
  state?: USState["value"];
  providerPreference: ProviderPreference;
  provider?: Provider | null;
  scheduleDate: ScheduleDate;
  language: AvailableLanguage;
  appointmentType: ScheduleAppointment["type"];
  appointmentToReschedule?: Appointment;
  subscriptionId?: Subscription["id"];
  vendor?: ScheduleAppointment["vendor"];

  actions: {
    setProviderPreference: (providerPreference: ProviderPreference) => void;
    setProvider: (provider?: Provider) => void;
    setScheduleDate: React.Dispatch<React.SetStateAction<ScheduleDate>>;
    setLanguage: (language: AvailableLanguage) => void;
  };
}

type ProviderPreference = ObjectValue<typeof PROVIDER_PREFERENCE>;

const ScheduleContext = createContext<ScheduleContextProps | null>(null);

export const useScheduleContext = () => {
  const context = useContext(ScheduleContext);

  if (!context) {
    throw new Error("useSchedule must be used within a <ScheduleProvider />");
  }

  return context;
};

interface ScheduleContextProvider extends PropsWithChildren {
  appointmentToReschedule?: Appointment;
  defaultLanguage: AvailableLanguage;
  appointmentType: ScheduleAppointment["type"];
  state?: USState["value"];
  subscriptionId?: Subscription["id"];
  vendor?: ScheduleAppointment["vendor"];
}

export const ScheduleContextProvider = ({
  appointmentToReschedule,
  children,
  defaultLanguage,
  appointmentType,
  state,
  subscriptionId,
  vendor,
}: ScheduleContextProvider) => {
  const [providerPreference, setProviderPreference] =
    useState<ProviderPreference>(
      appointmentToReschedule?.id
        ? PROVIDER_PREFERENCE.CUSTOM
        : PROVIDER_PREFERENCE.EARLIEST,
    );
  const [provider, setProvider] = useState(appointmentToReschedule?.provider);
  const [scheduleDate, setScheduleDate] = useState<ScheduleDate>({
    dateFromMonth: defaultDateFromMonth,
  });
  const [language, setLanguage] = useState(defaultLanguage);

  return (
    <ScheduleContext.Provider
      value={{
        state,
        providerPreference,
        provider,
        scheduleDate,
        language,
        appointmentType: appointmentToReschedule?.type ?? appointmentType,
        appointmentToReschedule,
        subscriptionId,
        vendor,

        actions: {
          setProviderPreference,
          setProvider,
          setScheduleDate,
          setLanguage,
        },
      }}
    >
      {children}
    </ScheduleContext.Provider>
  );
};
